import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, map, shareReplay, switchMap } from 'rxjs';

import { BaseService } from './base-service';
import { enrichResult } from '../utils/errors';
import {
  CreateWorkPackageStatusRequest,
  UpdateWorkPackageStatusRequest,
  WorkPackageStatusDto,
  WorkPackageStatusDtoPage,
} from '@api/backend-connector/models';
import { WorkPackageStatusesApi } from '@api/backend-connector/services';

const toasts = {
  createSuccessSummary: 'i18n.WorkPackageStatusToast.createSuccessSummary',
  createSuccessDetail: 'i18n.WorkPackageStatusToast.createSuccessDetail',
  createFailSummary: 'i18n.WorkPackageStatusToast.createFailSummary',
  createFailDetail: 'i18n.WorkPackageStatusToast.createFailDetail',

  updateSuccessSummary: 'i18n.Toast.updateSuccessSummary',
  updateSuccessDetail: 'i18n.Toast.updateSuccessDetail',
  updateFailSummary: 'i18n.Toast.updateFailSummary',
  updateFailDetail: 'i18n.Toast.updateFailDetail',

  deleteSuccessSummary: 'i18n.WorkPackageStatusToast.deleteSuccessSummary',
  deleteSuccessDetail: 'i18n.WorkPackageStatusToast.deleteSuccessDetail',
  deleteFailSummary: 'i18n.WorkPackageStatusToast.deleteFailSummary',
  deleteFailDetail: 'i18n.WorkPackageStatusToast.deleteFailDetail',
};

@Injectable({
  providedIn: 'root',
})
export class WorkPackageStatusService extends BaseService {
  private readonly workPackageStatusApi = inject(WorkPackageStatusesApi);
  readonly toasts = toasts;

  readonly refresh$ = new BehaviorSubject<void>(undefined);

  listWorkPackageStatuses(
    params: Parameters<typeof WorkPackageStatusService.prototype.workPackageStatusApi.listWorkPackageStatuses>[0],
  ): Observable<WorkPackageStatusDtoPage> {
    return this.workPackageStatusApi.listWorkPackageStatuses(params);
  }

  private readonly allWorkPackageStatuses$ = this.refresh$
    .pipe(switchMap(() => this.listWorkPackageStatuses({ sorting: ['position'] })))
    .pipe(
      map(({ items }) => items),
      shareReplay({ refCount: false, bufferSize: 1 }),
    );

  allWorkPackageStatuses(options?: { excludeIds?: string[] }) {
    return this.allWorkPackageStatuses$.pipe(
      map((items) => items.filter((i) => (options?.excludeIds ? !options.excludeIds.includes(i.id) : true))),
    );
  }

  allActiveWorkPackageStatuses(options?: { excludeIds?: string[] }) {
    return this.allWorkPackageStatuses(options).pipe(map((arr) => arr.filter((s) => s.isActive)));
  }

  getWorkPackageStatus(params: { id: string }): Observable<WorkPackageStatusDto> {
    return this.workPackageStatusApi.getWorkPackageStatus({
      ...params,
    });
  }

  createWorkPackageStatus(request: CreateWorkPackageStatusRequest) {
    return this.workPackageStatusApi.createWorkPackageStatus({ body: request });
  }

  updateWorkPackageStatus(id: string, body: UpdateWorkPackageStatusRequest) {
    return this.workPackageStatusApi.updateWorkPackageStatus({ id, body }).pipe(enrichResult());
  }

  deleteWorkPackageStatus(params: { id: string }) {
    return this.workPackageStatusApi.deleteWorkPackageStatus(params);
  }
}
